import React from "react";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import {
  ConfirmPopUp,
  deactivationGuardOf,
} from "../../../../../components/PopUp";
import type { ConfirmDetail } from "../../../../../components/PopUp/confirmShared/types";
import { valuationMessageFor } from "../../../../../services/valuation";
import { formatCurrency } from "../../../../../utils";
import { ActivateAccountModalProps } from "./types";

export const ActivateAccountModal: React.FC<ActivateAccountModalProps> = ({
  isOpen,
  account,
  onClose,
  onConfirm,
  linkedBrokers = [],
  isCheckingLinkedBrokers = false,
}) => {
  const { name, institution, isActive, currentValue, transactionsCount } =
    account;

  const transactionsLabel = `${transactionsCount.toLocaleString()} transaction${
    transactionsCount === 1 ? "" : "s"
  }`;
  const subtitle = [institution, transactionsLabel].filter(Boolean).join(" · ");

  const detail: ConfirmDetail = {
    icon: <AccountBalanceRoundedIcon />,
    title: name,
    subtitle,
    amount:
      currentValue === null
        ? valuationMessageFor(account.valuation)
        : formatCurrency(currentValue),
    amountVariant:
      currentValue !== null && currentValue < 0 ? "expense" : "neutral",
  };

  const deactivateText =
    "It will be hidden from your active accounts and excluded from totals by default. Transactions stay intact, and you can reactivate any time.";
  const reactivateText =
    "It will return to your active accounts list and be included in totals again. You can deactivate it later at any time.";

  const deactivation = deactivationGuardOf({
    text: deactivateText,
    linkedBrokers,
    isChecking: isCheckingLinkedBrokers,
  });

  const title = isActive ? `Deactivate ${name}?` : `Reactivate ${name}?`;
  const text = isActive ? deactivation.content : reactivateText;
  const isConfirmDisabled = isActive && deactivation.isConfirmDisabled;
  const confirmLabel = isActive ? "Deactivate account" : "Reactivate account";
  const loadingLabel = isActive ? "Deactivating…" : "Reactivating…";
  const icon = isActive ? (
    <PauseCircleOutlineRoundedIcon />
  ) : (
    <PlayCircleOutlineRoundedIcon />
  );

  return (
    <ConfirmPopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      tone="warning"
      icon={icon}
      title={title}
      text={text}
      details={detail}
      confirmLabel={confirmLabel}
      loadingLabel={loadingLabel}
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
    />
  );
};
